import { Form } from 'formik';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FC, useRef, useState } from 'react';
import * as Yup from 'yup';
import { validations } from '../../../../utils/validations';
import { IconButton, Modal as QcsModal } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { FormContainer } from '../../../common/form/FormContainer';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SupplierEmployeeAutocomplete } from '../../../common/form/SupplierEmployeeAutocomplete';
import { SupplierAutocomplete } from '../../../common/form/SupplierAutocomplete';
import { QcsMinimalistToggle } from '../../../common/form/QcsMinimalistToggle';
import { SectionHeader } from '../../../common/SectionHeader';
import { QcsAutocomplete } from '../../../common/form/QcsAutocomplete';
import { FieldWithAction } from '../../../common/form/FieldWithAction';
import { RightDrawer } from '../../../common/RightDrawer';
import { DrawerContent } from '../../../common/DrawerContent';
import { SupplierEmployeeDetail } from '../../../supplier/SupplierEmployeeDetail';
import { QcsHoverExpandButton } from '../../../common/basic/QcsHoverExpandButton';
import { getLangNameObj, getWorkplaceNameObj } from '../../../../utils/format';
import { useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import { hasRole, isExternal } from '../../../../utils/roles';
import { PathKey, useNavigation } from '../../../../utils/urlPaths';
import { useBusinessSettings } from '../../../../hooks/useBusinessSettings';
import { useInvitation } from './invitationFunctions';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { UserState } from '@api/models/enums/UserState';
import { UserRole } from '@api/models/enums/UserRole';
import { doNothing } from '../../../../utils/shared';

export interface InvitedPersonsAddModalData {
  supplierEmployee: SupplierEmployeeDto;
  activities: ReferenceDto[];
  workplaces: ReferenceDto[];
}

interface InvitedPersonsAddModalForm {
  subcontractor: ReferenceDto | null;
  supplierEmployee: SupplierEmployeeDto | null;
  activities: ReferenceDto[];
  workplaces: ReferenceDto[];
}

interface Props {
  supplierId?: string;
  initActivities: ReferenceDto[];
  initWorkplaces: ReferenceDto[];
  open: boolean;
  onClose: (data?: InvitedPersonsAddModalData) => Promise<void>;
  excludeIds: string[];
}

export const InvitedPersonsAddModal: FC<Props> = ({ supplierId, initActivities, initWorkplaces, open, onClose, excludeIds }) => {
  const { t, i18n, invitation, callWithLock } = useInvitation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSubcontractor, setIsSubcontractor] = useState(false);
  const [subcontractorId, setSubcontractorId] = useState<string | undefined>(undefined);
  const setFieldValueRef = useRef<(field: string, value: unknown) => void>(doNothing);
  const identity = useAppSelector(selectIdentity);
  const { navigate } = useNavigation();
  const businessSettings = useBusinessSettings();

  // Supplier managers may request a subcontractor be added when the feature is enabled for the company.
  const canRequestSubcontractor = hasRole(identity.roles, [UserRole.ExternalManager]) && !!businessSettings?.allowSubcontractors;

  const personSourceOptions = [
    { value: false, label: t('invitation.persons.supplierEmployee') },
    { value: true, label: t('invitation.persons.subcontractorEmployee') },
  ];

  // In subcontractor mode the selected subcontractor's employees are offered; otherwise the invitation's own supplier.
  const effectiveSupplierId = isSubcontractor ? subcontractorId : supplierId;

  // The subcontractor switch is only offered when the company allows subcontractors and the invitation's supplier is allowed to borrow employees.
  const canBorrowEmployees = !!businessSettings?.allowSubcontractors && !!invitation.supplier?.canBorrowEmployees;

  // External (supplier) users may only create employees for their own supplier, never for a subcontractor.
  const canCreateEmployee = !!effectiveSupplierId && !(isSubcontractor && isExternal(identity));

  const handleSubmit = async (data: InvitedPersonsAddModalForm) => {
    await callWithLock(async () => {
      const data2: InvitedPersonsAddModalData = {
        supplierEmployee: data.supplierEmployee!,
        activities: data.activities,
        workplaces: data.workplaces,
      };

      await onClose(data2);
    });
  };

  return (
    <>
      <QcsModal open={open} onClose={() => onClose()}>
        <QcsModalBox>
          <SectionHeader title="invitation.persons.add" />
          <FormikDebounce<InvitedPersonsAddModalForm>
            initialValues={{
              subcontractor: null,
              supplierEmployee: null,
              activities: initActivities,
              workplaces: initWorkplaces,
            }}
            validationSchema={Yup.object().shape({
              supplierEmployee: validations.objectRequired(t),
              activities: validations.arrayNotEmpty(t),
              workplaces: validations.arrayNotEmpty(t),
            })}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => {
              setFieldValueRef.current = setFieldValue;
              return (
                <Form>
                  <FormContainer>
                    {canBorrowEmployees && (
                      <>
                        <QcsMinimalistToggle
                          options={personSourceOptions}
                          value={isSubcontractor}
                          onChange={(val) => {
                            setIsSubcontractor(val);
                            setSubcontractorId(undefined);
                            setFieldValue('subcontractor', null);
                            setFieldValue('supplierEmployee', null);
                          }}
                        />

                        {isSubcontractor && (
                          <FieldWithAction
                            action={
                              canRequestSubcontractor && (
                                <QcsHoverExpandButton
                                  icon={<AddCircleOutline />}
                                  expandDirection="left"
                                  color="primary"
                                  ariaLabel={t('supplierRequest.requestButton')}
                                  sx={{ mt: '8px' }}
                                  onClick={() => navigate(PathKey.SupplierRequest, { invitationId: invitation.id! })}
                                >
                                  {t('supplierRequest.requestButton')}
                                </QcsHoverExpandButton>
                              )
                            }
                          >
                            <SupplierAutocomplete
                              name="subcontractor"
                              label={t('invitation.persons.subcontractor')}
                              required
                              canLendEmployees
                              onChange={(value) => {
                                setSubcontractorId((value as ReferenceDto | null)?.id);
                                setFieldValue('supplierEmployee', null);
                              }}
                            />
                          </FieldWithAction>
                        )}
                      </>
                    )}

                    <FieldWithAction
                      action={
                        canCreateEmployee && (
                          <IconButton onClick={() => setDrawerOpen(true)} color="primary" sx={{ mt: '8px' }} title={t('invitation.persons.addNew')}>
                            <AddCircleOutline />
                          </IconButton>
                        )
                      }
                    >
                      <SupplierEmployeeAutocomplete
                        name="supplierEmployee"
                        label={t('invitation.persons.person')}
                        required
                        disabled={isSubcontractor && !subcontractorId}
                        supplierId={effectiveSupplierId}
                        excludeIds={excludeIds}
                        state={[UserState.Active, UserState.Invited]}
                      />
                    </FieldWithAction>

                    <QcsAutocomplete
                      name="activities"
                      label={t('invitation.persons.activities')}
                      required={true}
                      multiple={true}
                      options={invitation.activities?.map((x) => ({
                        id: x.activity?.id,
                        name: x.activity?.name,
                        nameEn: x.activity?.nameEn,
                      }))}
                      getOptionId={(option) => option.id!}
                      getOptionName={(option) => getLangNameObj(i18n, option)}
                      loading={false}
                      loadingError={false}
                    />
                    <QcsAutocomplete
                      name="workplaces"
                      label={t('invitation.persons.workplaces')}
                      required={true}
                      multiple={true}
                      options={invitation.workplaces?.map((x) => ({
                        id: x.workplace?.id,
                        name: getWorkplaceNameObj(i18n, x.workplace, x.establishment),
                      }))}
                      getOptionId={(option) => option.id!}
                      getOptionName={(option) => getLangNameObj(i18n, option)}
                      loading={false}
                      loadingError={false}
                    />

                    <SubmitButton />
                  </FormContainer>
                </Form>
              );
            }}
          </FormikDebounce>
        </QcsModalBox>
      </QcsModal>

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} width={600} zIndex={1300}>
        {drawerOpen && effectiveSupplierId && (
          <DrawerContent>
            <SupplierEmployeeDetail
              createNew
              forSupplierId={effectiveSupplierId}
              forceRole={UserRole.ExternalWorker}
              onSaved={(createdEmployee?: SupplierEmployeeDto) => {
                if (createdEmployee) {
                  setFieldValueRef.current('supplierEmployee', createdEmployee);
                }
                setDrawerOpen(false);
              }}
            />
          </DrawerContent>
        )}
      </RightDrawer>
    </>
  );
};
